import { ColumnChart, LineChart, type ChartSeries } from '@platform/design-system';
import { usePageContext } from '../../usePageContext.ts';
import { seriesUnitOf } from '../overview-options.ts';
import { chartPointsOf, type PerformanceReport, type SeriesMetric } from '../performance.ts';
import { tickFormatOf } from './series-ticks.ts';
import { usePerformanceFor } from './widget-queries.ts';
import type { WidgetProps } from './widget-props.ts';
import { WidgetBody } from './WidgetBody.tsx';

const scaleOf = (measure: SeriesMetric) => (measure === 'sales' ? 'money' : 'count');

const comparisonSeriesOf = (
  report: PerformanceReport,
  measure: SeriesMetric,
  label: string,
  locale: string,
): ChartSeries | undefined =>
  report.comparison === null
    ? undefined
    : {
        key: 'comparison',
        label,
        points: chartPointsOf({ ...report, series: report.comparison.series }, measure, locale),
      };

export const TrendWidget = ({ widget, context }: WidgetProps<'trend'>) => {
  const { t, locale } = usePageContext();
  const { measure, chart, group } = widget.settings;
  const performance = usePerformanceFor(context, { unit: seriesUnitOf(group, context.range) });
  const report = performance.data;
  const label = t(`dashboard.widgets.trendTitle.${measure}`);
  const formatTick = tickFormatOf(scaleOf(measure), locale);
  const points = report === undefined ? [] : chartPointsOf(report, measure, locale);
  return (
    <WidgetBody query={performance}>
      {chart === 'columns' ? (
        <ColumnChart
          axis="grid"
          label={label}
          points={points}
          formatTick={formatTick}
          loading={performance.isPending}
        />
      ) : (
        <LineChart
          label={label}
          series={[{ key: measure, label, points }]}
          comparison={
            report === undefined
              ? undefined
              : comparisonSeriesOf(report, measure, t('dashboard.widgets.comparisonSeries'), locale)
          }
          variant={chart}
          formatTick={formatTick}
          emptyTitle={t('dashboard.noOrdersTitle')}
          loading={performance.isPending}
        />
      )}
    </WidgetBody>
  );
};
